import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    CreateGeneratedImageDefinitionInput,
    CreateGeneratedImageStoreAssociationInput,
    DeleteGeneratedImageEditorFileInput,
    FileEditorInformation,
    GeneratedImageDefinition,
    GeneratedImageStoreAssociation,
    GraphDotnetService,
    graphql,
    MediaclipMutationCreateGeneratedImageDefinitionArgs,
    MediaclipMutationCreateGeneratedImageStoreAssociationArgs,
    MediaclipMutationDeleteGeneratedImageEditorFileArgs,
    MediaclipMutationUpdateGeneratedImageDefinitionArgs,
    MediaclipMutationUpdateGeneratedImageStoreAssociationArgs,
    MediaclipMutationUploadGeneratedImageEditorFileArgs,
    Store,
    UpdateGeneratedImageDefinitionInput,
    UpdateGeneratedImageStoreAssociationInput,
    UploadGeneratedImageEditorInput,
} from 'app/shared/graph';

@Injectable({ providedIn: 'root' })
export class GeneratedImagesService {
    private readonly graphDotnetService = inject(GraphDotnetService);

    listForGlobal(): Observable<GeneratedImageDefinition[]> {
        return this.graphDotnetService.query(graphql`
                query getGeneratedImageDefinitions {
                    generatedImageDefinitions {
                        id
                        displayName
                        defaultAlias
                        description
                    }
                }
            `,
        ).pipe(map(e => e.generatedImageDefinitions));
    }

    listForSubscription(subscriptionId: string): Observable<GeneratedImageDefinition[]> {
        return this.graphDotnetService.query(graphql`
                query getSubscriptionGeneratedImageDefinitions($subscriptionId: UUID!) {
                    subscription(id: $subscriptionId) {
                        generatedImages {
                            id
                            displayName
                            defaultAlias
                            description
                            subscriptionId
                        }
                    }
                }
            `,
            { subscriptionId },
        ).pipe(map(e => e.subscription.generatedImages));
    }

    loadDefinition(definitionId: any): Observable<GeneratedImageDefinition> {
        return this.graphDotnetService.query(graphql`
                query getGeneratedImageDefinition($definitionId: UUID!) {
                    generatedImageDefinition(id: $definitionId) {
                        id
                        version
                        displayName
                        kind
                        defaultAlias
                        description
                        endpoint
                        preserveAspectRatio
                        maxWidth
                        maxHeight
                        warning
                        fileEditorInformation {
                            filename
                            version
                            url
                            mimeType
                        }
                        configs {
                            headers {
                                name
                                value
                                isSecret
                            }
                            additionalProperties {
                                name
                                value
                                isSecret
                            }
                            editorParameters {
                                name
                                value
                                isSecret
                            }
                        }
                    }
                }
            `,
            { definitionId: definitionId },
        ).pipe(map(e => e.generatedImageDefinition));
    }

    listAvailableDefinitionForStore(storeId: string): Observable<GeneratedImageDefinition[]> {
        return this.graphDotnetService.query(graphql`
                query getAvailableGeneratedImageDefinitions($storeId: String!) {
                    store(id: $storeId) {
                        id
                        subscriptionId
                        availableGeneratedImageDefinitions {
                            id
                            displayName
                            defaultAlias
                            description
                            subscriptionId
                        }
                    }
                }
            `,
            { storeId },
        ).pipe(map(e => e.store.availableGeneratedImageDefinitions));
    }

    listStoreAssociations(storeId: string): Observable<Store> {
        return this.graphDotnetService.query(graphql`
                query getGeneratedImageStoreAssociation($storeId: String!) {
                    store(id: $storeId) {
                        id
                        subscriptionId
                        generatedImageAssociations {
                            id
                            alias
                            storeId
                            definition {
                                id
                                displayName
                                kind
                                description
                                warning
                            }
                        }
                    }
                }
            `,
            { storeId },
        ).pipe(map(e => e.store));
    }

    loadStoreAssociation(storeId: string, associationId: string): Observable<GeneratedImageStoreAssociation> {
        return this.graphDotnetService.query(graphql`
                query getGeneratedImageStoreAssociation($storeId: String!, $associationId: UUID!) {
                    store(id: $storeId) {
                        generatedImageAssociation(id: $associationId) {
                            id
                            alias
                            storeId
                            definition {
                                id
                                displayName
                                kind
                                description
                                warning
                            }
                            configs {
                                headers {
                                    name
                                    value
                                }
                                additionalProperties {
                                    name
                                    value
                                }
                                editorParameters {
                                    name
                                    value
                                }
                            }
                        }
                    }
                }
            `,
            { storeId, associationId },
        ).pipe(map(e => e.store.generatedImageAssociation));
    }

    createGeneratedImageDefinition(input: CreateGeneratedImageDefinitionInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationCreateGeneratedImageDefinitionArgs>(graphql`
                mutation createGeneratedImageDefinition($input: CreateGeneratedImageDefinitionInput!) {
                    createGeneratedImageDefinition(input: $input)
                }
            `, { input },
        ).pipe(map(x => x.createGeneratedImageDefinition));
    }

    updateGeneratedImageDefinition(input: UpdateGeneratedImageDefinitionInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateGeneratedImageDefinitionArgs>(graphql`
                mutation updateGeneratedImageDefinition($input: UpdateGeneratedImageDefinitionInput!) {
                    updateGeneratedImageDefinition(input: $input)
                }
            `, { input },
        ).pipe(map(x => x.updateGeneratedImageDefinition));
    }

    createGeneratedImageStoreAssociation(input: CreateGeneratedImageStoreAssociationInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationCreateGeneratedImageStoreAssociationArgs>(graphql`
                mutation createGeneratedImageStoreAssociation($input: CreateGeneratedImageStoreAssociationInput!) {
                    createGeneratedImageStoreAssociation(input: $input)
                }
            `, { input },
        ).pipe(map(x => x.createGeneratedImageDefinition));
    }

    updateGeneratedImageStoreAssociation(input: UpdateGeneratedImageStoreAssociationInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateGeneratedImageStoreAssociationArgs>(graphql`
                mutation updateGeneratedImageStoreAssociation($input: UpdateGeneratedImageStoreAssociationInput!) {
                    updateGeneratedImageStoreAssociation(input: $input)
                }
            `, { input },
        ).pipe(map(x => x.updateGeneratedImageDefinition));
    }

    uploadAssetEditorFile(input: UploadGeneratedImageEditorInput): Observable<FileEditorInformation> {
        return this.graphDotnetService.mutate<MediaclipMutationUploadGeneratedImageEditorFileArgs>(graphql`
                mutation uploadGeneratedImageEditorFile($input: UploadGeneratedImageEditorInput!) {
                    uploadGeneratedImageEditorFile(input: $input) {
                        filename
                        url
                        mimeType
                    }
                }
            `, { input },
        ).pipe(map(x => x.uploadGeneratedImageEditorFile));
    }

    deleteAssetEditorFile(input: DeleteGeneratedImageEditorFileInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteGeneratedImageEditorFileArgs>(graphql`
                mutation deleteGeneratedImageEditorFile($input: DeleteGeneratedImageEditorFileInput!) {
                    deleteGeneratedImageEditorFile(input: $input)
                }
            `, { input },
        ).pipe(map(x => x.deleteGeneratedImageEditorFile));
    }

    deleteGeneratedImageAssociation(id: string): Observable<void> {
        return this.graphDotnetService.mutate(graphql`
            mutation deleteGeneratedImageStoreAssociation($id: UUID!) {
                deleteGeneratedImageStoreAssociation(id: $id)
            }`, { id })
            .pipe(map(x => x.deleteGeneratedImageStoreAssociation));
    }
}
